import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiEdit, FiTrash2, FiClock, FiTrendingUp, FiTarget, FiCalendar, FiExternalLink, FiHeart } = FiIcons;

const DailyTracker = () => {
  const [entries, setEntries] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    words: '',
    notes: '',
    timeOfDay: 'morning'
  });

  const formRef = useRef(null);

  useEffect(() => {
    const savedEntries = localStorage.getItem('gospelwise-entries');
    if (savedEntries) {
      setEntries(JSON.parse(savedEntries));
    }
  }, []);

  useEffect(() => {
    if (showAddForm && formRef.current) {
      formRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start',
        inline: 'nearest'
      });
    }
  }, [showAddForm]);

  const saveEntries = (newEntries) => {
    setEntries(newEntries);
    localStorage.setItem('gospelwise-entries', JSON.stringify(newEntries));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.words || formData.words <= 0) return;

    const entry = {
      id: editingEntry?.id || Date.now(),
      date: formData.date,
      words: parseInt(formData.words),
      notes: formData.notes,
      timeOfDay: formData.timeOfDay,
      createdAt: editingEntry?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    let newEntries;
    if (editingEntry) {
      newEntries = entries.map(e => e.id === editingEntry.id ? entry : e);
    } else {
      newEntries = [...entries, entry];
    }

    // Sort by date (newest first)
    newEntries.sort((a, b) => new Date(b.date) - new Date(a.date));
    saveEntries(newEntries);

    setFormData({
      date: new Date().toISOString().split('T')[0],
      words: '',
      notes: '',
      timeOfDay: 'morning'
    });
    setShowAddForm(false);
    setEditingEntry(null);
  };

  const handleEdit = (entry) => {
    setFormData({
      date: entry.date,
      words: entry.words.toString(),
      notes: entry.notes,
      timeOfDay: entry.timeOfDay
    });
    setEditingEntry(entry);
    setShowAddForm(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this entry?')) {
      const newEntries = entries.filter(e => e.id !== id);
      saveEntries(newEntries);
    }
  };

  const totalWords = entries.reduce((sum, entry) => sum + entry.words, 0);
  const thisWeekEntries = entries.filter(entry => {
    const entryDate = new Date(entry.date);
    const today = new Date();
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    return entryDate >= weekAgo;
  });
  const weeklyTotal = thisWeekEntries.reduce((sum, entry) => sum + entry.words, 0);
  const dailyAverage = entries.length > 0 ? Math.round(totalWords / entries.length) : 0;

  // Find most productive time
  const timeStats = entries.reduce((acc, entry) => {
    acc[entry.timeOfDay] = (acc[entry.timeOfDay] || 0) + entry.words;
    return acc;
  }, {});
  const mostProductiveTime = Object.keys(timeStats).reduce((a, b) => 
    timeStats[a] > timeStats[b] ? a : b, Object.keys(timeStats)[0]
  );

  const formatDate = (dateString) => {
    // Get today's date in YYYY-MM-DD format (local timezone)
    const today = new Date();
    const todayString = today.getFullYear() + '-' + 
      String(today.getMonth() + 1).padStart(2, '0') + '-' + 
      String(today.getDate()).padStart(2, '0');
    
    // Get yesterday's date in YYYY-MM-DD format (local timezone)
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayString = yesterday.getFullYear() + '-' + 
      String(yesterday.getMonth() + 1).padStart(2, '0') + '-' + 
      String(yesterday.getDate()).padStart(2, '0');
    
    if (dateString === todayString) return 'Today';
    if (dateString === yesterdayString) return 'Yesterday';
    
    // For other dates, parse and format normally
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="bg-white/80 backdrop-blur-md rounded-2xl p-6 border border-white/20 shadow-lg">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-slate-800">Daily Writing Log</h2>
            <p className="text-slate-600">Track your daily writing progress</p>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowAddForm(true)}
            className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white px-6 py-3 rounded-xl font-medium flex items-center gap-2 shadow-lg hover:shadow-xl transition-all duration-300"
          >
            <SafeIcon icon={FiPlus} />
            Add Entry
          </motion.button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <SafeIcon icon={FiTarget} className="text-blue-600" />
              <span className="text-sm font-medium text-slate-600">Total Words</span>
            </div>
            <p className="text-xl md:text-2xl font-bold text-slate-800">{totalWords.toLocaleString()}</p>
          </div>
          <div className="bg-green-50 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <SafeIcon icon={FiCalendar} className="text-green-600" />
              <span className="text-sm font-medium text-slate-600">This Week</span>
            </div>
            <p className="text-xl md:text-2xl font-bold text-slate-800">{weeklyTotal.toLocaleString()}</p>
          </div>
          <div className="bg-purple-50 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <SafeIcon icon={FiTrendingUp} className="text-purple-600" />
              <span className="text-sm font-medium text-slate-600">Daily Avg</span>
            </div>
            <p className="text-xl md:text-2xl font-bold text-slate-800">{dailyAverage.toLocaleString()}</p>
          </div>
          <div className="bg-orange-50 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <SafeIcon icon={FiClock} className="text-orange-600" />
              <span className="text-sm font-medium text-slate-600">Best Time</span>
            </div>
            <p className="text-lg md:text-xl font-bold text-slate-800 capitalize">
              {mostProductiveTime || 'N/A'}
            </p>
          </div>
        </div>
      </div>

      {/* Add/Edit Form */}
      <AnimatePresence>
        {showAddForm && (
          <motion.div
            ref={formRef}
            initial={{ opacity: 0, scale: 0.95, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -20 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="bg-white/80 backdrop-blur-md rounded-2xl p-6 border border-white/20 shadow-lg relative"
          >
            <div className="absolute top-0 left-6 right-6 h-1 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-t-2xl"></div>
            <h3 className="text-xl font-bold text-slate-800 mb-4 mt-2">
              {editingEntry ? 'Edit Entry' : 'Add New Entry'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Date</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Words Written</label>
                  <input
                    type="number"
                    value={formData.words}
                    onChange={(e) => setFormData({...formData, words: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="500"
                    min="1"
                    required
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Time of Day</label>
                  <select
                    value={formData.timeOfDay}
                    onChange={(e) => setFormData({...formData, timeOfDay: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="morning">Morning</option>
                    <option value="afternoon">Afternoon</option>
                    <option value="evening">Evening</option>
                    <option value="night">Night</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Notes (Optional)</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="How did the writing session go? Any insights or challenges?"
                  rows="3"
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white px-6 py-3 rounded-xl font-medium flex-1 hover:shadow-lg transition-all duration-300"
                >
                  {editingEntry ? 'Update Entry' : 'Save Entry'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setEditingEntry(null);
                    setFormData({
                      date: new Date().toISOString().split('T')[0],
                      words: '',
                      notes: '',
                      timeOfDay: 'morning'
                    });
                  }}
                  className="bg-slate-200 text-slate-700 px-6 py-3 rounded-xl font-medium hover:bg-slate-300 transition-all duration-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Recent Entries */}
      <div className="bg-white/80 backdrop-blur-md rounded-2xl p-6 border border-white/20 shadow-lg">
        <h3 className="text-xl font-bold text-slate-800 mb-4">Recent Entries</h3>
        {entries.length === 0 ? (
          <div className="text-center py-8">
            <SafeIcon icon={FiTarget} className="text-4xl text-slate-400 mx-auto mb-4" />
            <p className="text-slate-600 mb-4">No entries yet. Start tracking your daily writing!</p>
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white px-6 py-3 rounded-xl font-medium"
            >
              Add Your First Entry
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {entries.slice(0, 10).map((entry, index) => {
              // Check if this entry is from today using the same logic as formatDate
              const today = new Date();
              const todayString = today.getFullYear() + '-' + 
                String(today.getMonth() + 1).padStart(2, '0') + '-' + 
                String(today.getDate()).padStart(2, '0');
              const isToday = entry.date === todayString;
              
              return (
                <motion.div
                  key={entry.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`p-4 rounded-xl border transition-all duration-200 hover:shadow-md ${
                    isToday 
                      ? 'bg-blue-50 border-blue-200 shadow-sm' 
                      : 'bg-slate-50 border-slate-200 hover:bg-slate-100'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`font-medium ${isToday ? 'text-blue-800' : 'text-slate-800'}`}>
                          {formatDate(entry.date)}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                          isToday ? 'bg-blue-100 text-blue-700' : 'bg-slate-200 text-slate-600'
                        }`}>
                          {entry.timeOfDay}
                        </span>
                        <span className={`font-bold ${isToday ? 'text-blue-800' : 'text-slate-800'}`}>
                          {entry.words.toLocaleString()} words
                        </span>
                      </div>
                      {entry.notes && (
                        <p className="text-slate-600 text-sm">{entry.notes}</p>
                      )}
                    </div>
                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={() => handleEdit(entry)}
                        className="p-2 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200"
                      >
                        <SafeIcon icon={FiEdit} />
                      </button>
                      <button
                        onClick={() => handleDelete(entry.id)}
                        className="p-2 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200"
                      >
                        <SafeIcon icon={FiTrash2} />
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
            {entries.length > 10 && (
              <p className="text-center text-slate-500 text-sm mt-4">
                Showing 10 most recent entries
              </p>
            )}
          </div>
        )}
      </div>

      {/* Training CTA */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-2xl p-6 border border-purple-100 shadow-lg"
      >
        <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
          <div className="flex items-start gap-4 flex-1">
            <div className="bg-purple-100 p-3 rounded-full flex-shrink-0">
              <SafeIcon icon={FiHeart} className="text-2xl text-purple-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-slate-800 mb-1">Need godly encouragement to keep going?</h3>
              <p className="text-slate-600 text-sm">Get practical biblical wisdom and accountability to finish your writing project.</p>
            </div>
          </div>
          <motion.a
            href="https://www.crossandpen.com/resources/faithfull-finished"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-6 py-3 rounded-xl font-medium flex items-center gap-2 shadow-lg hover:shadow-xl transition-all duration-300 w-full lg:w-auto justify-center lg:justify-start"
          >
            Get Faithfully Finished
            <SafeIcon icon={FiExternalLink} className="text-sm" />
          </motion.a>
        </div>
      </motion.div>
    </div>
  );
};

export default DailyTracker;